import $ from 'jquery';
import Swal from 'sweetalert2';
import { getAxiosClient } from '~src/api';
import { onErrorShowMessage } from '~src/errors';
import { getState } from '~src/state';
import { triggerStateChangedRequest } from '~core';
import { changeImageSrc } from '~src/utils';
import { getMetadata } from '~loader/loader-utils';
import { trackEvent } from '~src/tracking';
import { onCompleteAttachment } from '~uploader';
import {
  handleTabAttachments,
  displaySidebarAttachments,
  fetchEditorAttachments,
} from './sidebarLegacy';
import { EdgeResizer } from '~src/utils';
import { prepareImageForEditing } from '~core/image';
import {
  startAiLoader,
  stopAiLoader,
  updateAiLoaderForSaving,
  showAiSuccessMessage,
  sleep,
  AI_FEATURE_LOADING_MESSAGES,
} from '~core/aiLoader';
import { isPlaceholderEditing } from '~core/magicFrame';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';
import { postEncryptedGiottoApiPayload } from '~core/aiBackground/promptEncryption';

const imageEnhancementHandler = (
  options = { algorithm: 'CodeFormer', weight: 1, enhanceBg: false }
) => async () => {
  const startTime = performance.now();

  // Determine feature name for tracking and credit check
  let featureName;
  if (options.algorithm === 'CodeFormer' && options.enhanceBg) {
    featureName = 'Image Enhancement';
  } else if (options.algorithm === 'CodeFormer' && options.weight === 0.5) {
    featureName = 'Make Look Younger';
  } else {
    featureName = 'Face Enhancement';
  }

  // Check credits before starting the operation
  const hasCredits = await checkAiCreditsBeforeUse({
    featureName,
    requiredCredits: 1,
  });

  if (!hasCredits) {
    return;
  }

  let selection = window.canvas.getActiveObject();
  if (selection.type === 'MagicFrame') {
    selection = selection.editingImg;
  }

  if (isPlaceholderEditing(selection)) {
    return;
  }

  const actualHeight = selection.height;
  const imageUrl = await prepareImageForEditing(selection, false);
  if (!imageUrl) {
    console.error('No image URL found in current selection', selection);
    return;
  }

  let magicFrame = selection._magicFrame;
  if (selection.type === 'MagicFrame') {
    magicFrame = window.canvas.getActiveObject();
  }

  const isRemoveBg =
    (magicFrame ? magicFrame : selection).giotto?.isRemoveBg ?? false;
  const isBlurBg =
    (magicFrame ? magicFrame : selection).giotto?.isBlurBg ?? false;
  const isErased =
    typeof (magicFrame ? magicFrame : selection).eraser !== 'undefined';

  if (isErased || (isRemoveBg && !isBlurBg)) {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Sei sicuro di procedere? 🧪',
      text:
        'Continuando, le parti trasparenti diventeranno nere. Questa funzione è ancora in fase sperimentale e andrebbe usata prima di rimuovere lo sfondo o usare la gomma.',
      showCancelButton: true,
      confirmButtonText: 'Procedi Comunque',
      cancelButtonText: 'Cancella',
      confirmButtonColor: '#278e8e',
    });

    if (!result.isConfirmed) {
      return;
    }
  }

  // Determine which loading messages to use based on the algorithm
  let loadingMessages;
  let title;
  if (options.algorithm === 'CodeFormer' && options.enhanceBg) {
    loadingMessages = AI_FEATURE_LOADING_MESSAGES.ENHANCE_RESOLUTION;
    title = 'Migliora Risoluzione';
  } else if (options.algorithm === 'CodeFormer' && options.weight === 0.5) {
    loadingMessages = AI_FEATURE_LOADING_MESSAGES.MAKE_YOUNGER;
    title = 'Filtro Bellezza';
  } else {
    loadingMessages = AI_FEATURE_LOADING_MESSAGES.ENHANCE_FACE;
    title = 'Migliora Viso';
  }

  const loaderInstance = startAiLoader(loadingMessages, `✨ ${title}`);

  if (!imageUrl) {
    await Swal.fire({
      text: 'Devi selezionare una foto per poter procedere',
      icon: 'warning',
      confirmButtonColor: '#278e8e',
      didOpen: () => {
        Swal.hideLoading();
      },
    });
    return;
  }

  let maxAttempts = 30;

  const httpClient = await getAxiosClient();
  const httpClientWithRetries = await getAxiosClient(true, maxAttempts);

  const metadata = getMetadata();

  let imageUrlSafe = imageUrl;

  if (!imageUrl.includes('removebg/')) {
    imageUrlSafe =
      actualHeight > 1500
        ? new EdgeResizer(imageUrl).scaleToHeight(
            Math.floor(Math.floor(actualHeight / 2))
          )
        : imageUrl;
  }

  const data = {
    version: options.algorithm,
    enhanceBg: options.enhanceBg,
    weight: options.weight,
    imageUrl: imageUrlSafe,
    canvasId: window.editorLoader.canvasId,
    notes: JSON.stringify(
      Object.assign({}, metadata, {
        canvasId: window.editorLoader.canvasId,
      })
    ),
  };

  const servizio = getState('selected.servizio', {});

  if (servizio && servizio.objectId) {
    data.servizioId = servizio.objectId;
  }

  let response;

  try {
    response = await postEncryptedGiottoApiPayload(
      httpClient,
      `${process.env.GIOTTO_API_URL}/enhancement`,
      data
    );
  } catch (err) {
    console.error(err);

    trackEvent('AI Image Enhancement', {
      success: false,
      time_taken: parseInt(performance.now() - startTime, 10),
      version: options.algorithm,
      enhance_bg: options.enhanceBg,
      weight: options.weight,
    });

    stopAiLoader();
    return onErrorShowMessage();
  }

  let isComplete = false;
  let currentAttempt = 1;
  let getStatusResponse;

  while (!isComplete && currentAttempt <= maxAttempts) {
    await sleep(1000);

    try {
      console.debug(
        `Get Enhancement Progress Attempt ${currentAttempt} of ${maxAttempts}`
      );
      getStatusResponse = await httpClientWithRetries.get(
        `${process.env.GIOTTO_API_URL}/enhancement/${response.data.enhancementId}`
      );
    } catch (err) {
      // swallow
    }

    if (getStatusResponse?.data?.progress?.enhancedImageS3Key) {
      isComplete = true;
    }

    currentAttempt++;
  }

  if (currentAttempt >= maxAttempts) {
    trackEvent('AI Image Enhancement', {
      success: false,
      time_taken: parseInt(performance.now() - startTime, 10),
      version: options.algorithm,
      enhance_bg: options.enhanceBg,
      weight: options.weight,
    });

    stopAiLoader();
    return onErrorShowMessage();
  }

  const url = `${process.env.EDGE_RESIZER}/${getStatusResponse.data.progress.enhancedImageS3Key}`;

  // Update loader to show saving step
  updateAiLoaderForSaving(loaderInstance);

  if (magicFrame) {
    await magicFrame.updatePerson(url);
    magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
    magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
  } else {
    const image = window.canvas.getActiveObject();

    const currentProps = {
      scaleX: image.scaleX,
      scaleY: image.scaleY,
      top: image.top,
      left: image.left,
    };

    await changeImageSrc(image, url);

    image.set({
      ...currentProps,
    });
  }
  window.canvas.renderAll();
  triggerStateChangedRequest();

  await onCompleteAttachment({
    successful: [
      {
        name: 'Miglioramento Risoluzione',
        uploadURL: url,
        type: 'image/jpg',
        size: 0,
        customAttachmentProps: {
          enhancementRawImage: imageUrl,
          isImageEnhancement: true,
        },
      },
    ],
  });

  const { attachments } = await fetchEditorAttachments(true);
  window.editorConfig.sidebarAttachments = attachments;
  displaySidebarAttachments();
  handleTabAttachments();

  // Show success message
  stopAiLoader();
  showAiSuccessMessage(title, 'La foto è stata elaborata con successo!');

  trackEvent('AI Image Enhancement', {
    success: true,
    time_taken: parseInt(performance.now() - startTime, 10),
    version: options.algorithm,
    enhance_bg: options.enhanceBg,
    weight: options.weight,
  });
};

export const initImageEnhancementListeners = async () => {
  $('body').on(
    'click',
    '#editor-menu-image-faceEnhancement',
    imageEnhancementHandler({
      algorithm: 'CodeFormer',
      weight: 1,
      enhanceBg: false,
    })
  );

  $('body').on(
    'click',
    '#editor-menu-image-imageEnhancement',
    imageEnhancementHandler({
      algorithm: 'CodeFormer',
      weight: 1,
      enhanceBg: true,
    })
  );

  $('body').on(
    'click',
    '#editor-menu-image-makeLookYounger',
    imageEnhancementHandler({
      algorithm: 'CodeFormer',
      weight: 0.5,
      enhanceBg: false,
    })
  );
};
